import { useState } from 'react';
import { useRessource } from '../hooks/useRessource';
import { ErreurApi, api } from '../lib/apiClient';
import { formaterDate, formaterMontant } from '../lib/format';
import { Badge, Champ, Infobulle } from './Elements';
import type { VariablePaie } from '../types';

/**
 * Les VARIABLES DE PAIE d'une ecole (2026-09-30) : la cotisation
 * mutuelle et la valeur du point d'anciennete, par periode. Une periode
 * sans fin vaut pour tous les mois a venir ; poser une periode ferme la
 * precedente a la veille de son debut — c'est le serveur qui le fait.
 *
 * Le bloc vit dans la fiche de l'entite mais a sa propre vie : chaque
 * geste ecrit immediatement, comme le logo, sans passer par le bouton
 * Enregistrer du formulaire. Une periode qui couvre un mois de paie
 * cloture est verrouillee.
 */

const VARIABLES: { cle: VariablePaie['cle']; libelle: string }[] = [
  { cle: 'cotisation_mutuelle', libelle: 'Cotisation mutuelle' },
  { cle: 'valeur_point_anciennete', libelle: 'Valeur du point d’ancienneté' },
];

const libelleDe = (cle: VariablePaie['cle']) => VARIABLES.find((v) => v.cle === cle)?.libelle ?? cle;

const enEuros = (centimes: number) => (centimes / 100).toFixed(2).replace('.', ',');
const enCentimes = (texte: string): number | null => {
  const n = Number(texte.replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
};

export function VariablesPaie({ entiteId, peutGerer }: { entiteId: string; peutGerer: boolean }) {
  const variables = useRessource(
    () => api.get<VariablePaie[]>(`/api/admin/entites/${entiteId}/variables-paie`),
    [entiteId],
  );
  const [saisie, setSaisie] = useState({
    cle: 'cotisation_mutuelle' as VariablePaie['cle'],
    valeur: '',
    dateDebut: '',
    dateFin: '',
  });
  const [edition, setEdition] = useState<{ id: string; valeur: string; dateFin: string } | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  const agir = async (travail: () => Promise<unknown>) => {
    setErreur(null);
    setEnCours(true);
    try {
      await travail();
      variables.recharger();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const ajouter = () => {
    const valeur = enCentimes(saisie.valeur);
    if (valeur === null || !saisie.dateDebut) {
      setErreur('Indiquez une valeur et une date de début.');
      return;
    }
    void agir(async () => {
      await api.post(`/api/admin/entites/${entiteId}/variables-paie`, {
        cle: saisie.cle,
        valeur,
        dateDebut: saisie.dateDebut,
        dateFin: saisie.dateFin || null,
      });
      setSaisie({ ...saisie, valeur: '', dateDebut: '', dateFin: '' });
    });
  };

  const enregistrerEdition = () => {
    if (!edition) return;
    const valeur = enCentimes(edition.valeur);
    if (valeur === null) {
      setErreur('Valeur invalide : utilisez le format 19,55.');
      return;
    }
    void agir(async () => {
      await api.patch(`/api/admin/entites/${entiteId}/variables-paie/${edition.id}`, {
        valeur,
        dateFin: edition.dateFin || null,
      });
      setEdition(null);
    });
  };

  const supprimer = (v: VariablePaie) => {
    if (!window.confirm(`Supprimer cette période de « ${libelleDe(v.cle)} » ?`)) return;
    void agir(() => api.delete(`/api/admin/entites/${entiteId}/variables-paie/${v.id}`));
  };

  const aujourdhui = new Date().toISOString().slice(0, 10);
  const enCoursDeValidite = (v: VariablePaie) =>
    v.date_debut <= aujourdhui && (v.date_fin === null || v.date_fin >= aujourdhui);

  // Les periodes VIVANTES d'abord — fin vide ou a venir : celles qui
  // valent aujourd'hui ou demain —, puis l'historique. Dans chaque
  // groupe, par variable et la periode la plus recente en tete.
  const vivante = (v: VariablePaie) => v.date_fin === null || v.date_fin >= aujourdhui;
  const lignes = [...(variables.donnees ?? [])].sort(
    (a, b) =>
      Number(vivante(b)) - Number(vivante(a)) ||
      libelleDe(a.cle).localeCompare(libelleDe(b.cle), 'fr') ||
      b.date_debut.localeCompare(a.date_debut),
  );

  return (
    <fieldset className="space-y-3 rounded-md border border-encre-200 p-3">
      <legend className="px-1 text-xs font-medium uppercase tracking-wide text-encre-500">
        Variables de paie
      </legend>

      {variables.erreur && <p className="text-sm text-red-700">{variables.erreur}</p>}

      {peutGerer && (
        <div className="grid grid-cols-[1fr_6rem_8.5rem_8.5rem_auto] items-end gap-2 border-b border-encre-200 pb-3">
          <Champ libelle="Variable">
            <select
              className="champ py-1.5"
              value={saisie.cle}
              onChange={(e) => setSaisie({ ...saisie, cle: e.target.value as VariablePaie['cle'] })}
            >
              {VARIABLES.map((v) => (
                <option key={v.cle} value={v.cle}>
                  {v.libelle}
                </option>
              ))}
            </select>
          </Champ>
          <Champ libelle="Valeur">
            <input
              className="champ tabulaire py-1.5 text-right"
              placeholder="19,55"
              value={saisie.valeur}
              onChange={(e) => setSaisie({ ...saisie, valeur: e.target.value })}
            />
          </Champ>
          <Champ libelle="Du">
            <input
              type="date"
              className="champ py-1.5"
              value={saisie.dateDebut}
              onChange={(e) => setSaisie({ ...saisie, dateDebut: e.target.value })}
            />
          </Champ>
          <Champ libelle="Au">
            <input
              type="date"
              className="champ py-1.5"
              value={saisie.dateFin}
              min={saisie.dateDebut || undefined}
              onChange={(e) => setSaisie({ ...saisie, dateFin: e.target.value })}
            />
          </Champ>
          {/* Une etiquette vide au-dessus du bouton : il se cale ainsi sur
              la ligne des champs, qui portent chacun la leur. */}
          <div className="block">
            <span className="etiquette invisible">Ajouter</span>
            <button type="button" className="bouton-secondaire block py-1.5" disabled={enCours} onClick={ajouter}>
              Ajouter
            </button>
          </div>
        </div>
      )}

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-encre-500">
            <th className="py-1 pr-2 font-medium">Variable</th>
            <th className="py-1 pr-2 text-right font-medium">Valeur</th>
            <th className="py-1 pr-2 font-medium">Du</th>
            <th className="py-1 pr-2 font-medium">Au</th>
            <th />
          </tr>
        </thead>
        <tbody className="divide-y divide-encre-100">
          {lignes.length === 0 && !variables.chargement && (
            <tr>
              <td colSpan={5} className="py-2 text-encre-400">
                Aucune variable : la paie comptera la mutuelle et la prime d’ancienneté à zéro, et le dira.
              </td>
            </tr>
          )}
          {lignes.map((v) =>
            edition?.id === v.id ? (
              <tr key={v.id} className="bg-encre-50">
                <td className="py-1.5 pr-2">{libelleDe(v.cle)}</td>
                <td className="py-1.5 pr-2">
                  <input
                    className="champ tabulaire py-1 text-right"
                    value={edition.valeur}
                    onChange={(e) => setEdition({ ...edition, valeur: e.target.value })}
                  />
                </td>
                <td className="tabulaire py-1.5 pr-2 text-encre-500">{formaterDate(v.date_debut)}</td>
                <td className="py-1.5 pr-2">
                  <input
                    type="date"
                    className="champ py-1"
                    value={edition.dateFin}
                    min={v.date_debut}
                    onChange={(e) => setEdition({ ...edition, dateFin: e.target.value })}
                  />
                </td>
                <td className="whitespace-nowrap py-1.5 text-right">
                  <button type="button" className="text-xs underline" disabled={enCours} onClick={enregistrerEdition}>
                    Enregistrer
                  </button>
                  <button type="button" className="ml-2 text-xs text-encre-500 underline" onClick={() => setEdition(null)}>
                    Annuler
                  </button>
                </td>
              </tr>
            ) : (
              <tr key={v.id} className={v.verrouillee ? 'text-encre-400' : ''}>
                <td className="py-1.5 pr-2">
                  {libelleDe(v.cle)}
                  {enCoursDeValidite(v) && (
                    <Badge ton="succes">
                      <span className="ml-0">en cours</span>
                    </Badge>
                  )}
                </td>
                <td className="tabulaire py-1.5 pr-2 text-right">{formaterMontant(v.valeur)}</td>
                <td className="tabulaire py-1.5 pr-2">{formaterDate(v.date_debut)}</td>
                <td className="tabulaire py-1.5 pr-2">{v.date_fin ? formaterDate(v.date_fin) : '—'}</td>
                <td className="whitespace-nowrap py-1.5 text-right">
                  {v.verrouillee ? (
                    <Infobulle texte="Cette période a servi à une paie clôturée : elle ne se modifie plus.">
                      <span className="text-xs">🔒</span>
                    </Infobulle>
                  ) : (
                    peutGerer && (
                      <>
                        <button
                          type="button"
                          className="text-xs underline"
                          onClick={() => setEdition({ id: v.id, valeur: enEuros(v.valeur), dateFin: v.date_fin ?? '' })}
                        >
                          Modifier
                        </button>
                        <button type="button" className="ml-2 text-xs text-red-600 underline" onClick={() => supprimer(v)}>
                          Supprimer
                        </button>
                      </>
                    )
                  )}
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>


      {erreur && <p className="text-sm text-red-700">{erreur}</p>}

      <p className="text-xs text-encre-500">
        Une période sans date de fin vaut pour tous les mois à venir. Ajouter une période ferme la
        précédente à la veille de son début. Une période qui couvre un mois de paie clôturé ne se
        modifie plus ; pour corriger une valeur à sa vraie date, ajoutez une période — les nets
        des mois clôturés qu’elle couvre sont alors recalculés.
      </p>
    </fieldset>
  );
}
